<template>
  <section id="admin-transactions">
    <b-card class="admin-page-mobile">
      <!-- Page Header -->
      <div class="page-header mb-4">
        <div class="header-content">
          <h4>
            <v-icon size="28" class="ml-2">mdi-receipt-text</v-icon>
            لیست تراکنش‌ها
          </h4>
          <p>مدیریت و پیگیری تمامی تراکنش‌های پرداخت</p>
        </div>
        <div class="header-actions">
          <b-button variant="light" :disabled="exportLoading" @click="exportExcel">
            <b-spinner v-if="exportLoading" small class="ml-2" />
            <v-icon v-else size="18" class="ml-1">mdi-download</v-icon>
            خروجی Excel
          </b-button>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid mb-4">
        <div class="stat-card success">
          <div class="stat-icon">
            <v-icon color="white">mdi-check-circle</v-icon>
          </div>
          <div class="stat-value">{{ stats.success.count }}</div>
          <div class="stat-label">تراکنش موفق</div>
        </div>
        <div class="stat-card danger">
          <div class="stat-icon">
            <v-icon color="white">mdi-close-circle</v-icon>
          </div>
          <div class="stat-value">{{ stats.failed.count }}</div>
          <div class="stat-label">تراکنش ناموفق</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-icon">
            <v-icon color="white">mdi-clock-outline</v-icon>
          </div>
          <div class="stat-value">{{ stats.pending.count }}</div>
          <div class="stat-label">در انتظار</div>
        </div>
        <div class="stat-card primary">
          <div class="stat-icon">
            <v-icon color="white">mdi-cash-multiple</v-icon>
          </div>
          <div class="stat-value">{{ formatPrice(stats.total.amount / 10) }}</div>
          <div class="stat-label">مجموع درآمد (تومان)</div>
        </div>
      </div>

      <!-- Filters & Search -->
      <div class="filters-section mb-4">
        <div class="search-box">
          <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
          <input
            v-model="search"
            type="text"
            placeholder="جستجو بر اساس نام، موبایل، IMEI..."
            @input="onSearch"
          >
        </div>
        <div class="filter-tabs">
          <button
            v-for="tab in filterTabs"
            :key="tab.value"
            :class="['filter-tab', { active: activeFilter === tab.value }]"
            @click="setFilter(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2">در حال بارگذاری...</p>
      </div>

      <!-- Data Table -->
      <div v-else class="table-wrapper">
        <v-data-table
          :headers="headers"
          :items="transactions"
          :search="search"
          :loading="loading"
          loading-text="در حال بارگذاری..."
          no-data-text="تراکنشی یافت نشد"
          hide-default-footer
          class="elevation-0"
          mobile-breakpoint="0"
        >
          <template v-slot:item.orderId="{ item }">
            <span class="order-id">#{{ item.orderId }}</span>
          </template>

          <template v-slot:item.user="{ item }">
            <div class="user-info">
              <div class="user-avatar">
                {{ getInitials(item.userId ? item.userId.name : '', item.userId ? item.userId.family : '') }}
              </div>
              <div class="user-details">
                <span class="user-name">{{ item.userId ? item.userId.name : '' }} {{ item.userId ? item.userId.family : '' }}</span>
                <span class="user-mobile">{{ item.userId ? item.userId.mobile : '' }}</span>
              </div>
            </div>
          </template>

          <template v-slot:item.device="{ item }">
            <div class="device-info">
              <span class="device-title">{{ item.deviceId ? item.deviceId.title : '-' }}</span>
              <code class="device-imei">{{ item.deviceId ? item.deviceId.imei : '' }}</code>
            </div>
          </template>

          <template v-slot:item.amount="{ item }">
            <span class="amount">{{ formatPrice(item.amount / 10) }} <small>تومان</small></span>
          </template>

          <template v-slot:item.status="{ item }">
            <b-badge :variant="getStatusVariant(item.status)" pill class="status-badge">
              <v-icon size="14" class="ml-1">{{ getStatusIcon(item.status) }}</v-icon>
              {{ getStatusText(item.status) }}
            </b-badge>
          </template>

          <template v-slot:item.refNumber="{ item }">
            <span v-if="item.refNumber" class="ref-number">{{ item.refNumber }}</span>
            <span v-else class="text-muted">-</span>
          </template>

          <template v-slot:item.creationTime="{ item }">
            <div class="date-cell">
              {{ formatDate(item.creationTime) }}
              <small>{{ formatTime(item.creationTime) }}</small>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <b-button variant="link" size="sm" @click="showDetail(item)">
              <v-icon size="18">mdi-eye</v-icon>
            </b-button>
          </template>
        </v-data-table>

        <!-- Custom Pagination -->
        <div v-if="pagination.pages > 1" class="pagination-wrapper">
          <span class="pagination-info">
            نمایش {{ (pagination.page - 1) * pagination.limit + 1 }} تا {{ Math.min(pagination.page * pagination.limit, pagination.total) }} از {{ pagination.total }}
          </span>
          <div class="pagination-btns">
            <button
              class="page-btn"
              :disabled="pagination.page === 1"
              @click="goToPage(pagination.page - 1)"
            >
              <v-icon size="16">mdi-chevron-right</v-icon>
            </button>
            <button
              v-for="p in visiblePages"
              :key="p"
              :class="['page-btn', { active: p === pagination.page }]"
              @click="goToPage(p)"
            >
              {{ p }}
            </button>
            <button
              class="page-btn"
              :disabled="pagination.page === pagination.pages"
              @click="goToPage(pagination.page + 1)"
            >
              <v-icon size="16">mdi-chevron-left</v-icon>
            </button>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Detail Modal -->
    <b-modal
      ref="detail-modal"
      title="جزئیات تراکنش"
      centered
      size="lg"
      hide-footer
    >
      <div v-if="selectedTransaction" class="detail-content">
        <div class="detail-grid">
          <div class="detail-item">
            <span class="label">شماره سفارش</span>
            <span class="value">#{{ selectedTransaction.orderId }}</span>
          </div>
          <div class="detail-item">
            <span class="label">وضعیت</span>
            <b-badge :variant="getStatusVariant(selectedTransaction.status)" pill>
              {{ getStatusText(selectedTransaction.status) }}
            </b-badge>
          </div>
          <div class="detail-item">
            <span class="label">کاربر</span>
            <span class="value">{{ selectedTransaction.userId ? selectedTransaction.userId.name : '' }} {{ selectedTransaction.userId ? selectedTransaction.userId.family : '' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">موبایل</span>
            <span class="value" dir="ltr">{{ selectedTransaction.userId ? selectedTransaction.userId.mobile : '' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">دستگاه</span>
            <span class="value">{{ getDeviceTitle(selectedTransaction) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">IMEI</span>
            <code class="value">{{ selectedTransaction.deviceId ? selectedTransaction.deviceId.imei : '' }}</code>
          </div>
          <div class="detail-item">
            <span class="label">پلن</span>
            <span class="value">{{ selectedTransaction.planId ? selectedTransaction.planId.title : '' }} ({{ selectedTransaction.planId ? selectedTransaction.planId.period : '' }} ماهه)</span>
          </div>
          <div class="detail-item">
            <span class="label">مبلغ</span>
            <span class="value text-primary font-weight-bold">{{ formatPrice(selectedTransaction.amount / 10) }} تومان</span>
          </div>
          <div class="detail-item">
            <span class="label">تاریخ ایجاد</span>
            <span class="value">{{ formatDateTime(selectedTransaction.creationTime) }}</span>
          </div>
          <div v-if="selectedTransaction.paymentTime" class="detail-item">
            <span class="label">تاریخ پرداخت</span>
            <span class="value">{{ formatDateTime(selectedTransaction.paymentTime) }}</span>
          </div>
          <div v-if="selectedTransaction.refNumber" class="detail-item">
            <span class="label">شماره مرجع</span>
            <span class="value font-weight-bold">{{ selectedTransaction.refNumber }}</span>
          </div>
          <div v-if="selectedTransaction.trackingCode" class="detail-item">
            <span class="label">کد پیگیری</span>
            <span class="value">{{ selectedTransaction.trackingCode }}</span>
          </div>
          <div v-if="selectedTransaction.errorMessage" class="detail-item full-width">
            <span class="label">پیام خطا</span>
            <span class="value text-danger">{{ selectedTransaction.errorMessage }}</span>
          </div>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner, BModal,
} from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BButton,
    BBadge,
    BSpinner,
    BModal,
  },
  data() {
    return {
      loading: true,
      exportLoading: false,
      transactions: [],
      search: '',
      activeFilter: 'all',
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
      },
      stats: {
        total: { count: 0, amount: 0 },
        success: { count: 0, amount: 0 },
        failed: { count: 0, amount: 0 },
        pending: { count: 0, amount: 0 },
        cancelled: { count: 0, amount: 0 },
      },
      selectedTransaction: null,
      filterTabs: [
        { label: 'همه', value: 'all' },
        { label: 'موفق', value: 'success' },
        { label: 'ناموفق', value: 'failed' },
        { label: 'در انتظار', value: 'pending' },
      ],
      headers: [
        { text: 'شماره سفارش', value: 'orderId', sortable: true },
        { text: 'کاربر', value: 'user', sortable: false },
        { text: 'دستگاه', value: 'device', sortable: false },
        { text: 'مبلغ', value: 'amount', sortable: true },
        { text: 'وضعیت', value: 'status', sortable: true },
        { text: 'شماره مرجع', value: 'refNumber', sortable: false },
        { text: 'تاریخ', value: 'creationTime', sortable: true },
        { text: 'عملیات', value: 'actions', sortable: false },
      ],
      searchTimeout: null,
    }
  },
  computed: {
    visiblePages() {
      const pages = []
      const current = this.pagination.page
      const total = this.pagination.pages

      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)

      if (end - start < 4) {
        if (start === 1) {
          end = Math.min(total, 5)
        } else {
          start = Math.max(1, total - 4)
        }
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    },
  },
  async mounted() {
    this.$vuetify.rtl = true
    await this.loadStats()
    await this.loadTransactions()
  },
  methods: {
    async loadStats() {
      try {
        const res = await PaymentService.getAdminStats()
        if (res.status === 'success') {
          this.stats = res.result
        }
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    },

    async loadTransactions() {
      try {
        this.loading = true

        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
        }

        if (this.activeFilter !== 'all') {
          params.status = this.activeFilter
        }

        const res = await PaymentService.getAdminList(params)

        if (res.status === 'success') {
          this.transactions = res.result.payments || []
          this.pagination = res.result.pagination || this.pagination
        }
      } catch (error) {
        console.error('Error loading transactions:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'خطا در بارگذاری تراکنش‌ها',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    setFilter(filter) {
      this.activeFilter = filter
      this.pagination.page = 1
      this.loadTransactions()
    },

    onSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1
        this.loadTransactions()
      }, 500)
    },

    goToPage(page) {
      if (page >= 1 && page <= this.pagination.pages) {
        this.pagination.page = page
        this.loadTransactions()
      }
    },

    showDetail(tx) {
      this.selectedTransaction = tx
      this.$refs['detail-modal'].show()
    },

    async exportExcel() {
      try {
        this.exportLoading = true

        const params = {}
        if (this.activeFilter !== 'all') {
          params.status = this.activeFilter
        }

        const res = await PaymentService.exportExcel(params)

        if (res.status === 'success') {
          // Convert to CSV and download
          const data = res.result
          const headers = ['شماره سفارش', 'نام کاربر', 'موبایل', 'دستگاه', 'IMEI', 'پلن', 'مبلغ', 'وضعیت', 'شماره مرجع', 'تاریخ ایجاد']
          const csvContent = [
            headers.join(','),
            ...data.map(row => [
              row.orderId,
              `"${row.userName}"`,
              row.userMobile,
              `"${row.deviceTitle}"`,
              row.deviceImei,
              `"${row.planTitle}"`,
              row.amount / 10,
              row.status,
              row.refNumber,
              row.creationTime ? new Date(row.creationTime * 1000).toLocaleString('fa-IR') : '',
            ].join(','))
          ].join('\n')

          const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`
          link.click()

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'موفق',
              text: 'فایل دانلود شد',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      } catch (error) {
        console.error('Error exporting:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطا',
            text: 'خطا در خروجی گرفتن',
            icon: 'XIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.exportLoading = false
      }
    },

    getInitials(name, family) {
      const n = (name || '').charAt(0)
      const f = (family || '').charAt(0)
      return n + f || '؟'
    },

    getDeviceTitle(tx) {
      if (!tx.deviceId) return '-'
      return tx.deviceId.title || tx.deviceId.imei || '-'
    },

    getStatusVariant(status) {
      const variants = {
        success: 'success',
        failed: 'danger',
        pending: 'warning',
        cancelled: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusIcon(status) {
      const icons = {
        success: 'mdi-check',
        failed: 'mdi-close',
        pending: 'mdi-clock-outline',
        cancelled: 'mdi-cancel',
      }
      return icons[status] || 'mdi-help'
    },

    getStatusText(status) {
      const texts = {
        success: 'موفق',
        failed: 'ناموفق',
        pending: 'در انتظار',
        cancelled: 'انصراف',
      }
      return texts[status] || status
    },

    formatPrice(amount) {
      return new Intl.NumberFormat('fa-IR').format(amount)
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp * 1000).toLocaleDateString('fa-IR')
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp * 1000).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
    },

    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return `${date.toLocaleDateString('fa-IR')} - ${date.toLocaleTimeString('fa-IR')}`
    },
  },
}
</script>

<style scoped>
#admin-transactions {
  min-height: 100vh;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 20px;
  padding: 28px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  color: white;
}

.header-content h4 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.header-content p {
  margin: 0;
  opacity: 0.9;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 80px;
  height: 80px;
  border-radius: 0 0 0 80px;
  opacity: 0.1;
}

.stat-card.success::before { background: #22c55e; }
.stat-card.danger::before { background: #ef4444; }
.stat-card.warning::before { background: #f59e0b; }
.stat-card.primary::before { background: #0ea5e9; }

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.stat-card.success .stat-icon { background: linear-gradient(135deg, #22c55e, #16a34a); }
.stat-card.danger .stat-icon { background: linear-gradient(135deg, #ef4444, #dc2626); }
.stat-card.warning .stat-icon { background: linear-gradient(135deg, #f59e0b, #d97706); }
.stat-card.primary .stat-icon { background: linear-gradient(135deg, #0ea5e9, #0284c7); }

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
}

/* Filters */
.filters-section {
  background: white;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.search-box input:focus {
  outline: none;
  border-color: #0ea5e9;
  background: white;
}

.search-box .search-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.filter-tabs {
  display: flex;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
}

.filter-tab {
  padding: 10px 18px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-weight: 500;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-tab:hover {
  color: #0ea5e9;
}

.filter-tab.active {
  background: white;
  color: #0ea5e9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Table Wrapper */
.table-wrapper {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

/* Table Cells */
.order-id {
  font-weight: 700;
  color: #0ea5e9;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 12px;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
}

.user-mobile {
  font-size: 12px;
  color: #64748b;
  direction: ltr;
  text-align: right;
}

.device-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.device-title {
  font-size: 13px;
  color: #1e293b;
}

.device-imei {
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}

.amount {
  font-weight: 700;
  color: #22c55e;
}

.amount small {
  font-weight: 400;
  color: #64748b;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
}

.ref-number {
  font-family: monospace;
  color: #0ea5e9;
  font-size: 12px;
}

.date-cell {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  color: #475569;
}

.date-cell small {
  font-size: 11px;
  color: #94a3b8;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #f1f5f9;
}

.pagination-info {
  font-size: 13px;
  color: #64748b;
}

.pagination-btns {
  display: flex;
  gap: 8px;
}

.page-btn {
  min-width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: #0ea5e9;
  color: #0ea5e9;
}

.page-btn.active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Detail Modal */
.detail-content {
  padding: 8px 0;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.detail-item.full-width {
  grid-column: span 2;
}

.detail-item .label {
  font-size: 12px;
  color: #64748b;
}

.detail-item .value {
  font-size: 14px;
  color: #1e293b;
}

/* Responsive */
@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 20px;
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 20px;
  }

  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    width: 100%;
  }

  .filter-tabs {
    width: 100%;
    overflow-x: auto;
  }

  .filter-tab {
    white-space: nowrap;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-item.full-width {
    grid-column: span 1;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
